import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../../../../styles/TaskManagement.module.css';
import apiService from '../../../../services/api';
import { useNotification } from '../../../../contexts/NotificationContext';
import { usePageLoaderContext } from '../../../../contexts/PageLoaderContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { useConfirm } from '../../../../contexts/ConfirmContext';
import { TaskProposal, Task } from '../types';
import SidePanel from '../../../UI/SidePanel';
import TaskInitiationForm from '../components/TaskInitiationForm';
import SkeletonLoader from '../../../UI/SkeletonLoader';
import EmptyState from '../../../UI/EmptyState';
import TaskStats from '../components/TaskStats';
import { TaskStats as TaskStatsType } from '../types';

import TaskCard from '../components/TaskCard';

interface InitiativeListProps {
    filterType: 'initiatives' | 'proposals';
    onProposalClick?: (proposal: TaskProposal) => void;
}

const InitiativeList: React.FC<InitiativeListProps> = ({ filterType, onProposalClick }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const notificationService = useNotification();
    const { showLoader, hideLoader } = usePageLoaderContext();
    const confirmService = useConfirm();

    const [proposals, setProposals] = useState<TaskProposal[]>([]);
    const [createdTasks, setCreatedTasks] = useState<Task[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Template Modal State (for Self-Assigned Tasks & Custom Proposals)
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [availableTemplates, setAvailableTemplates] = useState<Task[]>([]);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

    useEffect(() => {
        loadData(filterType);
    }, [filterType]);

    const loadAvailableTemplates = async () => {
        setIsLoadingTemplates(true);
        try {
            const response = await apiService.getAvailableTemplates();
            if (response.status === 'success') {
                setAvailableTemplates(response.data || []);
            } else {
                throw new Error(response.message || 'Failed to load templates');
            }
        } catch (error: any) {
            console.error('Error loading templates:', error);
            notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load task templates');
            setAvailableTemplates([]);
        } finally {
            setIsLoadingTemplates(false);
        }
    };

    const loadData = async (type: 'initiatives' | 'proposals') => {
        setIsLoading(true);
        try {
            if (type === 'proposals') {
                const response = await apiService.get('/api/proposals/my_proposals/');
                const allProposals = (response as any).results || (Array.isArray(response) ? response : []) || [];
                setProposals(allProposals);
            } else {
                const taskResponse = await apiService.getSelfAssignedTasks();
                // Handle both wrapped and unwrapped responses for robustness
                const tasksData = (taskResponse as any).status === 'success' ? (taskResponse.data || []) : (taskResponse as any);
                setCreatedTasks(Array.isArray(tasksData) ? tasksData : []);
            }
        } catch (error: any) {
            console.error(`Error loading ${type}:`, error);
            notificationService.error(error.response?.data?.message || error.response?.data?.error || `Failed to load ${type}`);
        } finally {
            // Only turn off loading if this was the requested type for the current view
            // to avoid flickering if we are refreshing background data
            if (type === filterType) {
                setIsLoading(false);
            }
        }
    };

    const stats: TaskStatsType = React.useMemo(() => {
        let relevantTasks: Task[] = [];
        let relevantProposals: TaskProposal[] = [];

        if (filterType === 'initiatives') {
            relevantTasks = createdTasks;
        } else if (filterType === 'proposals') {
            relevantProposals = proposals;
        }

        const total = relevantProposals.length + relevantTasks.length;

        // Active Tasks: Check both Tasks and Proposals
        // For Tasks: Anything not completed or cancelled
        const activeInstanceTasks = relevantTasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled').length;
        // For Proposals: Pending approval
        const activeProposals = relevantProposals.filter(p => p.status === 'pending_approval').length;

        const inProgressTotal = activeInstanceTasks + activeProposals;

        // Completed:
        const completedInstanceTasks = relevantTasks.filter(t => t.status === 'completed').length;
        const approvedProposals = relevantProposals.filter(p => p.status === 'approved').length;
        const completedTotal = completedInstanceTasks + approvedProposals;

        // Points
        const points = relevantTasks.reduce((sum, t) => sum + (t.points_earned || 0), 0);

        return {
            totalTasks: total,
            inProgress: inProgressTotal,
            completed: completedTotal,
            pointsEarned: points
        };
    }, [proposals, createdTasks, filterType]);



    const handleWithdraw = async (proposal: TaskProposal) => {
        const confirmed = await confirmService.show(
            'Withdraw Request',
            'Are you sure you want to withdraw this request?',
            { okText: 'Withdraw', cancelText: 'Cancel' }
        );

        if (!confirmed) return;

        try {
            showLoader();
            await apiService.post(`/api/proposals/${proposal.id}/withdraw/`);
            notificationService.success('Request withdrawn');
            loadData(filterType);
        } catch (error: any) {
            notificationService.error(error.message || 'Failed to withdraw');
        } finally {
            hideLoader();
        }
    };

    return (
        <div className={styles.taskList}>
            {/* Render Stats if not loading and has data */}
            {(!isLoading && (proposals.length > 0 || createdTasks.length > 0)) && (
                <div style={{ marginBottom: '2rem' }}>
                    <TaskStats stats={stats} activePersonalTab="initiatives" />
                </div>
            )}

            <div className={styles.tasksContainer}>
                {isLoading ? (
                    <SkeletonLoader count={6} variant="card" />
                ) : ((filterType === 'initiatives' && createdTasks.length === 0) || (filterType === 'proposals' && proposals.length === 0)) ? (
                    <EmptyState
                        title={filterType === 'initiatives' ? "No Tasks Found" : "No Proposals Found"}
                        description={filterType === 'initiatives'
                            ? "You haven't created any tasks yet."
                            : "You haven't submitted any proposals yet."}
                        action={{
                            label: filterType === 'initiatives' ? "Create Task" : "New Proposal",
                            onClick: () => {
                                setShowTemplateModal(true);
                                loadAvailableTemplates();
                            }
                        }}
                    />
                ) : (
                    <>
                        {/* Render all tasks using TaskCard component */}
                        {filterType === 'initiatives' && createdTasks.map(task => (
                            <TaskCard
                                key={task.id}
                                task={task}
                                onClick={(t) => navigate(`/task-management/${t.id}`)}
                                isWorkMode={false}
                            />
                        ))}

                        {/* Render proposals with custom styling */}
                        {filterType === 'proposals' && proposals.map(proposal => (
                            <div
                                key={proposal.id}
                                className={styles.taskCard}
                                style={{ cursor: 'pointer' }}
                                onClick={() => onProposalClick?.(proposal)}
                            >
                                {/* Card Header */}
                                <div className={styles.cardHeader}>
                                    <div className={styles.cardHeaderLeft}>
                                        <div className={styles.taskIconWrapper} style={{ backgroundColor: '#EFF6FF', color: '#3B82F6' }}>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                <polyline points="14 2 14 8 20 8"></polyline>
                                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                            </svg>
                                        </div>
                                        <div className={styles.cardTitleSection}>
                                            <h3 className={styles.cardTitle}>{proposal.title}</h3>
                                            <div className={styles.cardDescription}>{proposal.description}</div>
                                        </div>
                                    </div>
                                    <div className={styles.cardHeaderRight}>
                                        <div className={`${styles.taskStatusBadge} ${(proposal.display_status || proposal.status) === 'approved'
                                            ? styles.statusApproved
                                            : (proposal.display_status || proposal.status) === 'rejected' ? styles.statusCancelled
                                                : (proposal.display_status || proposal.status) === 'pending_approval' ? styles.statusPendingApproval
                                                    : styles.statusPending
                                            }`}>
                                            {(proposal.display_status || proposal.status || '').replace('_', ' ').split(' ').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                        </div>

                                        {/* Actions: Withdraw */}
                                        {proposal.status === 'pending_approval' && (
                                            <div className={styles.cardActions}>
                                                <button
                                                    className={`${styles.actionButton}`}
                                                    onClick={(e) => { e.stopPropagation(); handleWithdraw(proposal); }}
                                                    title="Withdraw Request"
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.25rem',
                                                        padding: '0.5rem 1rem',
                                                        fontSize: '0.75rem',
                                                        borderRadius: '30px',
                                                        border: '1px solid #ef4444',
                                                        backgroundColor: '#ef4444',
                                                        color: 'white',
                                                        fontWeight: '600',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px',
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = '#dc2626';
                                                        e.currentTarget.style.borderColor = '#dc2626';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = '#ef4444';
                                                        e.currentTarget.style.borderColor = '#ef4444';
                                                    }}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <circle cx="12" cy="12" r="10"></circle>
                                                        <line x1="15" y1="9" x2="9" y2="15"></line>
                                                        <line x1="9" y1="9" x2="15" y2="15"></line>
                                                    </svg>
                                                    Withdraw
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Card Body - Tags */}
                                <div className={styles.cardBody}>
                                    <div className={styles.cardTags}>
                                        <div className={styles.categoryTag} style={{
                                            backgroundColor: '#3b82f615',
                                            color: '#2563eb',
                                            borderColor: '#3b82f640'
                                        }}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.375rem' }}>
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                <polyline points="14 2 14 8 20 8"></polyline>
                                            </svg>
                                            <span style={{ fontSize: '0.625rem', fontWeight: '500' }}>Request</span>
                                        </div>
                                        {proposal.current_stage && (
                                            <div className={styles.groupTag}>
                                                <span style={{ fontSize: '0.625rem' }}>Stage: {proposal.current_stage.replace(/_/g, ' ').toUpperCase()}</span>
                                            </div>
                                        )}
                                        {proposal.priority && (
                                            <div className={`${styles.priorityTag} ${styles[`priority${proposal.priority.charAt(0).toUpperCase() + proposal.priority.slice(1)}`]}`}>
                                                <span style={{ fontSize: '0.625rem' }}>{proposal.priority.charAt(0).toUpperCase() + proposal.priority.slice(1)} Priority</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Metadata */}
                                    <div className={styles.cardMetadata}>
                                        <div className={styles.metadataItem}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                                <line x1="3" y1="10" x2="21" y2="10"></line>
                                            </svg>
                                            <span className={styles.metadataText}>Submitted: {new Date(proposal.created_at).toLocaleDateString()}</span>
                                        </div>

                                        {proposal.requester_details && (
                                            <div className={styles.metadataItem}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
                                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                                    <circle cx="12" cy="7" r="4"></circle>
                                                </svg>
                                                <span className={styles.metadataText}>Requested by: {proposal.requester_details.full_name}</span>
                                            </div>
                                        )}
                                        {proposal.rejection_reason && (
                                            <div className={styles.metadataItem} style={{ color: '#EF4444' }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
                                                    <circle cx="12" cy="12" r="10"></circle>
                                                    <line x1="15" y1="9" x2="9" y2="15"></line>
                                                    <line x1="9" y1="9" x2="15" y2="15"></line>
                                                </svg>
                                                <span className={styles.metadataText}>Rejection: {proposal.rejection_reason}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                        )}
                    </>
                )}
            </div>

            {/* Task Instance Form Modal */}
            <TaskInitiationForm
                isOpen={showTemplateModal}
                onClose={() => setShowTemplateModal(false)}
                onSuccess={() => {
                    // Refresh both lists to ensure stats are updated and new items are available
                    loadData('initiatives');
                    loadData('proposals');
                }}
                availableTemplates={availableTemplates}
                isLoadingTemplates={isLoadingTemplates}
                onLoadTemplates={loadAvailableTemplates}
                enableCustomProposal={true}
            />
        </div>
    );
};

export default InitiativeList;
